import { useState, useRef, useEffect } from 'react';
import { Language } from '../types';
import { Settings, BarChart2, HelpCircle, Globe } from 'lucide-react';
import { TRANSLATIONS } from '../translations';

interface NavBarProps {
    language: Language;
    setLanguage: (lang: Language) => void;
    setShowHelp: (show: boolean) => void;
    setShowStats: (show: boolean) => void;
    setShowSettings: (show: boolean) => void;
    onLogoClick: () => void;
}

export default function NavBar({
    language,
    setLanguage,
    setShowHelp,
    setShowStats,
    setShowSettings,
    onLogoClick
}: NavBarProps) {
    const [showLangMenu, setShowLangMenu] = useState(false);
    const langMenuRef = useRef<HTMLDivElement>(null);
    const t = TRANSLATIONS[language];

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
                setShowLangMenu(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLangSelect = (lang: Language) => {
        setLanguage(lang);
        setShowLangMenu(false);
    };

    return (
        <header className="w-full flex flex-col items-center gap-4 py-6 px-4 z-50">
            {/* Header Icons & Logo */}
            <div className="w-full max-w-5xl flex justify-between items-center relative border-b border-white/10 pb-4">
                {/* Left icons - same width as right icons for centering */}
                <div className="flex gap-1 w-[120px]">
                    <button
                        type="button"
                        onClick={() => setShowSettings(true)}
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center text-stone-400 hover:text-[#6b9b7a] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#4a7c59] rounded-lg"
                        title={t.settings}
                        aria-label={t.settings}
                    >
                        <Settings size={22} strokeWidth={1.5} aria-hidden="true" />
                    </button>
                </div>

                <a
                    href="/"
                    onClick={(e) => {
                        e.preventDefault();
                        onLogoClick();
                    }}
                    className="flex flex-col items-center group focus:outline-none no-underline"
                    aria-label="AOTdle Home"
                >
                    <h1 className="text-3xl md:text-5xl font-bold text-center text-[#6b9b7a] tracking-widest drop-shadow-md group-hover:text-[#4a7c59] transition-colors duration-300">
                        AOTDLE
                    </h1>
                    <span className="text-[10px] md:text-xs font-sans tracking-[0.3em] text-stone-400 uppercase mt-1 group-hover:text-stone-300 transition-colors duration-300 drop-shadow-sm">{t.home_subtitle}</span>
                </a>

                {/* Right icons - same width as left icons for centering */}
                <div className="flex gap-1 w-[120px] justify-end">
                    <div className="relative" ref={langMenuRef}>
                        <button
                            type="button"
                            onClick={() => setShowLangMenu(!showLangMenu)}
                            className={`min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#4a7c59] rounded-lg ${showLangMenu ? 'text-[#6b9b7a]' : 'text-stone-400 hover:text-[#6b9b7a]'}`}
                            title={t.language || 'Language'}
                            aria-label={t.language || 'Language'}
                            aria-expanded={showLangMenu}
                            aria-haspopup="listbox"
                            aria-controls="language-menu"
                        >
                            <Globe size={22} strokeWidth={1.5} aria-hidden="true" />
                        </button>

                        {showLangMenu && (
                            <div id="language-menu" role="listbox" aria-label={t.language || 'Language'} className="absolute right-0 mt-2 w-32 bg-black/90 backdrop-blur-md border border-[#4a7c59]/50 shadow-xl rounded-lg py-1 z-50">
                                <button role="option" aria-selected={language === 'en'} onClick={() => handleLangSelect('en')} className={`w-full text-left px-4 py-3 text-sm hover:bg-white/10 transition-colors ${language === 'en' ? 'text-[#6b9b7a] font-bold' : 'text-stone-300'}`}>English</button>
                                <button role="option" aria-selected={language === 'es'} onClick={() => handleLangSelect('es')} className={`w-full text-left px-4 py-3 text-sm hover:bg-white/10 transition-colors ${language === 'es' ? 'text-[#6b9b7a] font-bold' : 'text-stone-300'}`}>Español</button>
                                <button role="option" aria-selected={language === 'tr'} onClick={() => handleLangSelect('tr')} className={`w-full text-left px-4 py-3 text-sm hover:bg-white/10 transition-colors ${language === 'tr' ? 'text-[#6b9b7a] font-bold' : 'text-stone-300'}`}>Türkçe</button>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowStats(true)}
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center text-stone-400 hover:text-[#6b9b7a] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#4a7c59] rounded-lg"
                        title={t.stats}
                        aria-label={t.stats}
                    >
                        <BarChart2 size={22} strokeWidth={1.5} aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowHelp(true)}
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center text-stone-400 hover:text-[#6b9b7a] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#4a7c59] rounded-lg"
                        title={t.help}
                        aria-label={t.help}
                    >
                        <HelpCircle size={22} strokeWidth={1.5} aria-hidden="true" />
                    </button>
                </div>
            </div>
        </header>
    );
}
